import { ReactNode, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { Typography } from "@digital-services/ui";

import ErrorComponent from "@components/ErrorComponent";
import LoadingComponent from "@components/LoadingComponent";

import {
  ApplicationJsonItemResponse,
  ApplicationJsonModel
} from "@applications/models/ApplicationJsonModel";
import { useGetAssociatedApplications } from "@applications/services/ApplicationService";

import {
  UserApplication,
  UserApplicationWithErrors
} from "../models/UserApplication";
import { EditApplicationAssociation } from "./EditApplicationAssociation";

interface ReturnValue {
  userAppAssocData?: UserApplication;
  userAppAssocError?: null | string;
}
const verifyUserAppAssociation = (
  userApplications: UserApplicationWithErrors[] | undefined,
  orgApplicationId: string
): ReturnValue => {
  const userAppById: UserApplicationWithErrors | undefined =
    userApplications?.find(
      (item: UserApplicationWithErrors) =>
        item?.data?.application.id === orgApplicationId
    );

  return {
    userAppAssocData: userAppById?.data,
    userAppAssocError: userAppById?.error
  };
};

interface Props {
  userId: string;
  applicationId?: string;
  userApplications: UserApplicationWithErrors[];
  isDisabled?: boolean;
  onAppRoleChange: (
    applicationId: string,
    applicationName: string,
    applicationRoles: string[],
    isValid: boolean
  ) => void;
  onAppRemove: (application: ApplicationJsonModel) => void;
}

export function EditApplicationAssociations(props: Readonly<Props>) {
  const {
    userId,
    applicationId,
    userApplications,
    isDisabled = false,
    onAppRoleChange,
    onAppRemove
  } = props;

  const { t } = useTranslation(undefined, { keyPrefix: "user.applications" });

  const {
    applications: orgApps,
    isLoading,
    error
  } = useGetAssociatedApplications();

  const renderChildren = useCallback(
    (children: ReactNode): ReactNode => {
      return (
        <>
          <Typography variant="smallTitle">{t("section_title")}</Typography>
          <div className="flex flex-col gap-5">{children}</div>
        </>
      );
    },
    [t]
  );

  if (isLoading)
    return renderChildren(<LoadingComponent message={t("load.message")} />);
  if (error)
    return renderChildren(<ErrorComponent message={t("load.error.message")} />);

  if (orgApps!.length === 0)
    return renderChildren(<>{t("load.none.message")}</>);

  if (!orgApps) return null;

  const filteredOrgApps: ApplicationJsonItemResponse[] = [];

  for (const item of orgApps) {
    if (item.error) {
      continue;
    }

    filteredOrgApps.push(item);
  }

  return renderChildren(
    <>
      {filteredOrgApps.map((appResponse: ApplicationJsonItemResponse) => {
        const orgApplication = appResponse.data;

        const { userAppAssocData, userAppAssocError } =
          verifyUserAppAssociation(userApplications, orgApplication.id);

        let isDisabledLocal = isDisabled;
        let isApplicationEnabledLocal = userAppAssocData !== undefined;

        if (userAppAssocError) {
          if (isApplicationEnabledLocal) {
            // because if Org Apps exists in User-App assoc it's NOT enough to "enable" checkbox
            isApplicationEnabledLocal = false;
          }
          isDisabledLocal = true;
        }

        if (applicationId === orgApplication.id || !applicationId) {
          return (
            <EditApplicationAssociation
              key={"appAssoc_" + orgApplication.id + "_" + userId}
              applicationId={orgApplication.id}
              applicationName={orgApplication.name}
              appEnv={orgApplication.appEnv}
              isApplicationEnabled={isApplicationEnabledLocal}
              isDisabled={isDisabledLocal}
              userAppAssocError={userAppAssocError}
              userRoles={userAppAssocData?.userRoles ?? ([] as string[])}
              onAppRoleChange={onAppRoleChange}
              onAppRemove={() => {
                const userApplication = userApplications.find(
                  (item: UserApplicationWithErrors) =>
                    item?.data?.application.id === orgApplication.id
                );
                if (userApplication) onAppRemove(orgApplication);
              }}
            />
          );
        }
      })}
    </>
  );
}
